import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Switch, FormControlLabel, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import adminService from '../../../services/adminService';
import AdminToolbar from '../components/AdminToolbar';

const PackageManagement = () => {
    const [packages, setPackages] = useState([]);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Form State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPkg, setEditingPkg] = useState(null);
    const [formData, setFormData] = useState({
        name: '', code: '', price: '', durationDays: '', description: '', isPopular: false, isActive: true
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchPackages();
    }, [search, filterStatus]); // Refetch when search/filter changes

    const fetchPackages = async () => {
        const params = {};
        if (search) params.search = search;
        if (filterStatus !== 'all') params.isActive = filterStatus === 'active';

        const res = await adminService.getAllPackages(params);
        if (res.success) setPackages(res.data);
    };

    // Helper: Format number to VN currency string (using comma separator)
    const formatInputMoney = (value) => {
        if (!value) return '';
        let str = String(value);
        // Handle decimal from DB (e.g. 1000.00 -> 1000) to avoid 100000
        if (str.includes('.') && !str.includes(',')) {
            str = str.split('.')[0];
        }
        const numberStr = str.replace(/\D/g, '');
        return numberStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // Helper: Parse formatted string back to number
    const parseInputMoney = (value) => {
        if (!value) return 0;
        return Number(String(value).split(',').join(''));
    };

    const handleCreate = () => {
        setEditingPkg(null);
        setFormData({ name: '', code: '', price: '', durationDays: '', description: '', isPopular: false, isActive: true });
        setErrors({});
        setOpenDialog(true);
    };

    const handleEdit = (pkg) => {
        setEditingPkg(pkg);
        setFormData({
            name: pkg.name,
            code: pkg.code,
            price: formatInputMoney(pkg.price), // Format matching table
            durationDays: pkg.durationDays,
            description: pkg.description || '',
            isPopular: pkg.isPopular,
            isActive: pkg.isActive
        });
        setErrors({});
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa gói cước này? hành động này không thể hoàn tác.')) {
            try {
                await adminService.deletePackage(id);
                toast.success('Đã xóa gói cước thành công!');
                fetchPackages();
            } catch (error) {
                toast.error('Lỗi: ' + error.message);
            }
        }
    };

    const handleSave = async () => {
        const newErrors = {};
        const rawPrice = parseInputMoney(formData.price);

        // Validation
        if (!formData.code) newErrors.code = 'Vui lòng nhập mã gói';
        if (!formData.name) newErrors.name = 'Vui lòng nhập tên gói';
        if (!formData.price) newErrors.price = 'Vui lòng nhập giá tiền';
        else if (rawPrice < 0) newErrors.price = 'Giá tiền không được nhỏ hơn 0';

        if (!formData.durationDays) newErrors.durationDays = 'Vui lòng nhập thời hạn';
        else if (Number(formData.durationDays) <= 0) newErrors.durationDays = 'Thời hạn phải lớn hơn 0';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Vui lòng kiểm tra lại thông tin!');
            return;
        }

        const submitData = { ...formData, price: rawPrice };

        try {
            if (editingPkg) {
                await adminService.updatePackage(editingPkg.id, submitData);
                toast.success('Cập nhật gói cước thành công!');
            } else {
                await adminService.createPackage(submitData);
                toast.success('Tạo gói cước mới thành công!');
            }
            fetchPackages();
            setOpenDialog(false);
        } catch (error) {
            toast.error('Lỗi: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleInputChange = (field, value) => {
        let newValue = value;
        if (field === 'price') {
            newValue = formatInputMoney(value);
        }
        setFormData({ ...formData, [field]: newValue });

        if (errors[field]) {
            setErrors({ ...errors, [field]: null });
        }
    };

    return (
        <Box>
            <ToastContainer position="top-right" autoClose={3000} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 3 }}>Quản Lý Gói Cước</Typography>

            <AdminToolbar
                searchValue={search}
                onSearchChange={setSearch}
                filterValue={filterStatus}
                onFilterChange={setFilterStatus}
                filterOptions={[
                    { value: 'all', label: 'Tất cả trạng thái' },
                    { value: 'active', label: 'Đang hoạt động' },
                    { value: 'inactive', label: 'Đã ẩn' }
                ]}
                onCreate={handleCreate}
                createLabel="Tạo Gói Mới"
                placeholder="Tìm tên hoặc mã gói..."
            />

            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Tên Gói</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Mã (Code)</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Giá (VND)</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Thời Hạn (Ngày)</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Ngày Tạo</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Trạng Thái</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Hành Động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {packages.map((pkg) => (
                            <TableRow key={pkg.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                <TableCell sx={{ color: 'white' }}>
                                    {pkg.name}
                                    {pkg.isPopular && <Typography variant="caption" sx={{ ml: 1, color: '#fbbf24', border: '1px solid #fbbf24', px: 0.5, borderRadius: 1 }}>POPULAR</Typography>}
                                </TableCell>
                                <TableCell sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>{pkg.code}</TableCell>
                                <TableCell sx={{ color: '#4ade80', fontWeight: 'bold' }}>{Number(pkg.price).toLocaleString()}</TableCell>
                                <TableCell sx={{ color: 'white' }}>{pkg.durationDays} ngày</TableCell>
                                <TableCell sx={{ color: 'white' }}>{new Date(pkg.createdAt).toLocaleDateString('vi-VN')}</TableCell>
                                <TableCell sx={{ color: pkg.isActive ? '#4ade80' : '#f87171' }}>
                                    {pkg.isActive ? 'Đang bán' : 'Ngừng bán'}
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEdit(pkg)} sx={{ color: '#60a5fa' }}><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleDelete(pkg.id)} sx={{ color: '#f87171' }}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit/Create Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPkg ? 'Chỉnh Sửa Gói' : 'Tạo Gói Mới'}</DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Mã Gói (Code)"
                            value={formData.code}
                            onChange={(e) => handleInputChange('code', e.target.value)}
                            required
                            error={!!errors.code}
                            helperText={errors.code}
                        />
                        <TextField
                            label="Tên Gói"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                        <Box display="flex" gap={2}>
                            <TextField
                                label="Giá Tiền (VND)"
                                value={formData.price}
                                onChange={(e) => handleInputChange('price', e.target.value)}
                                required
                                fullWidth
                                error={!!errors.price}
                                helperText={errors.price}
                            />
                            <TextField
                                label="Thời Hạn (Ngày)" type="number"
                                value={formData.durationDays}
                                onChange={(e) => handleInputChange('durationDays', e.target.value)}
                                required
                                fullWidth
                                error={!!errors.durationDays}
                                helperText={errors.durationDays}
                            />
                        </Box>
                        <TextField
                            label="Mô Tả" multiline rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                        />

                        <Box display="flex" justifyContent="space-between" alignItems="center" p={1} border="1px solid #ddd" borderRadius={1}>
                            <Typography>Trạng thái kinh doanh (Active)</Typography>
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            />
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center" p={1} border="1px solid #ddd" borderRadius={1}>
                            <Typography>Gói Phổ Biến (Popular)</Typography>
                            <Switch
                                checked={formData.isPopular}
                                onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Hủy</Button>
                    <Button onClick={handleSave} variant="contained">Lưu</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PackageManagement;
